/** @jsx jsx */
import React, { useCallback, useEffect, useRef } from 'react';
import { css, jsx } from '@compiled/react';
import FocusLock from 'react-focus-lock';
import Heading from '@atlaskit/heading';
import { Box, xcss, Text } from '@atlaskit/primitives';
import { N20 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { borderRadius } from '@atlassian/jira-common-styles/src/main.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { FormattedMessage } from '@atlassian/jira-intl';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { useFocusMarshal } from '@atlassian/timeline-table/common/context/side-effect-marshal/focus-marshal';
import { INCOMING, OUTGOING } from '../../../common/constants/dependency.tsx';
import { CHART_FLYOUT_Z_INDEX } from '../../../common/constants/z-index.tsx';
import type { Position } from '../../../common/types/common.tsx';
import type {
	Appearance,
	OnUnlinkCallback,
	OverlayItem,
	ImplicitDependencyItem,
} from '../../../common/types/dependency.tsx';
import DependencyCardContainer from '../../../common/ui/dependency/card/card-container/index.tsx';
import Card from '../../../common/ui/dependency/card/index.tsx';
import Separator from '../../../common/ui/dependency/separator/index.tsx';
import { OverlayFlyout } from '../../../common/ui/flyout/main.tsx';
import { isDependencyDatesOverlapping } from '../../../common/utils/dependency/overlap.tsx';
import { createUnlinkHandlerByAppearance } from '../../../common/utils/dependency/unlink-button.tsx';
import ImplicitDependency from './implicit-dependencies/index.tsx';
import Line from './line/index.tsx';
import messages from './messages.tsx';
import { useDependencyFlyoutLabel, useImplicitDependencyLabel } from './utils.tsx';

export type Props = {
	id: string;
	item: OverlayItem;
	position: Position;
	dependencies: OverlayItem[];
	dependees: OverlayItem[];
	implicitDependencyItems: ImplicitDependencyItem[];
	maybeInwardDependencyDescription: string | undefined;
	maybeOutwardDependencyDescription: string | undefined;
	triggerRef?: React.RefObject<HTMLElement>;
	onUnlink?: OnUnlinkCallback; // optional handler, controls visibility of the unlink button,
	onClose: () => void;
};

const RoadmapDependencyFlyout = ({ position, triggerRef, ...contentProps }: Props) => {
	const { formatMessage } = useIntl();

	const dialogContentRef = useRef<HTMLDivElement>(null);

	const {
		id,
		item,
		dependencies,
		dependees,
		implicitDependencyItems,
		onUnlink,
		onClose,
		maybeInwardDependencyDescription,
		maybeOutwardDependencyDescription,
	} = contentProps;

	const hasDependencies = dependencies.length > 0;
	const hasDependees = dependees.length > 0;
	const hasImplicitDependencies = implicitDependencyItems
		? implicitDependencyItems.length > 0
		: false;

	const { shouldPreventNavigation } = useFocusMarshal();

	const onUnlinkClick: OnUnlinkCallback = useCallback(
		(input, analyticsEvent) => {
			onUnlink?.(input, analyticsEvent);
			dialogContentRef.current?.focus();
		},
		[onUnlink],
	);

	const handleClose = useCallback(() => {
		shouldPreventNavigation(false);
		onClose();
	}, [shouldPreventNavigation, onClose]);

	useEffect(() => {
		if (!hasDependencies && !hasDependees && !hasImplicitDependencies) {
			if (fg('timeline_grid_navigation_m2')) {
				handleClose();
			} else {
				onClose();
			}
		}
	}, [hasDependencies, hasDependees, hasImplicitDependencies, onClose, handleClose]);

	const defaultInwardDependencyDescription = `${formatMessage(messages.blockedBy)}`;
	const defaultOutwardDependencyDescription = `${formatMessage(messages.blocks)}`;

	const inwardDependencyDescription =
		maybeInwardDependencyDescription ?? defaultInwardDependencyDescription;
	const outwardDependencyDescription =
		maybeOutwardDependencyDescription ?? defaultOutwardDependencyDescription;

	const renderDepsBlock = (deps: OverlayItem[], appearance: Appearance, label: string) => {
		if (!deps.length) {
			return null;
		}

		const isOverlapping = deps.some(({ startDate, dueDate }: OverlayItem) =>
			appearance === INCOMING
				? isDependencyDatesOverlapping(startDate, dueDate, item.startDate, item.dueDate)
				: isDependencyDatesOverlapping(item.startDate, item.dueDate, startDate, dueDate),
		);
		const isFiltered = deps.some(({ isHidden }: OverlayItem) => isHidden);
		const hasDepItems = appearance === INCOMING ? hasDependees : hasDependencies;

		const closeHandler = fg('timeline_grid_navigation_m2') ? handleClose : onClose;

		// Wrap the cards in a border container if there are multiple dependencies
		const DependencyCardContainerComponent =
			isVisualRefreshEnabled() && deps.length > 1 && fg('visual-refresh_drop_1')
				? DependencyCardContainer
				: 'div';
		return (
			<>
				<Card appearance={appearance} {...item} />
				<Separator
					label={label}
					unlinkLabel={
						deps.length === 1
							? `${item.itemKey} ${item.title} ${label} ${deps[0].itemKey} ${deps[0].title}`
							: undefined
					}
					isOverlapping={isOverlapping}
					isFiltered={isFiltered}
					{...(onUnlink && deps.length === 1
						? {
								onUnlink: createUnlinkHandlerByAppearance(
									item.id,
									deps[0].id,
									appearance,
									onUnlinkClick,
									hasDepItems || hasImplicitDependencies ? undefined : closeHandler,
								),
							}
						: undefined)}
				/>
				<DependencyCardContainerComponent>
					{/* wrapper element, required to apply nth-child styles */}
					{deps.map((dep: OverlayItem, index) => {
						const shouldApplyVisualRefreshChanges =
							isVisualRefreshEnabled() && fg('visual-refresh_drop_1');
						return (
							<Card
								isBottomCard={
									shouldApplyVisualRefreshChanges && deps.length > 1
										? index === deps.length - 1
										: undefined
								}
								isStacked={shouldApplyVisualRefreshChanges && deps.length > 1}
								key={dep.id}
								appearance={appearance === INCOMING ? OUTGOING : INCOMING}
								unlinkLabel={`${item.itemKey} ${item.title} ${label} ${dep.itemKey} ${dep.title}`}
								{...dep}
								isOverlapping={
									appearance === INCOMING
										? isDependencyDatesOverlapping(
												dep.startDate,
												dep.dueDate,
												item.startDate,
												item.dueDate,
											)
										: isDependencyDatesOverlapping(
												item.startDate,
												item.dueDate,
												dep.startDate,
												dep.dueDate,
											)
								}
								{...(onUnlink && deps.length > 1
									? {
											onUnlink: createUnlinkHandlerByAppearance(
												item.id,
												dep.id,
												appearance,
												onUnlinkClick,
												undefined,
											),
										}
									: undefined)}
							/>
						);
					})}
				</DependencyCardContainerComponent>
			</>
		);
	};

	const { itemKey, title } = item;

	const incomingIssuesDesc = useDependencyFlyoutLabel(
		item,
		dependencies,
		INCOMING,
		inwardDependencyDescription,
	);
	const outgoingIssuesDesc = useDependencyFlyoutLabel(
		item,
		dependees,
		OUTGOING,
		outwardDependencyDescription,
	);

	const implicitDependenciesLabel = useImplicitDependencyLabel(
		itemKey,
		title,
		implicitDependencyItems,
	);

	const flyoutAriaLabel = `${incomingIssuesDesc} ${outgoingIssuesDesc} ${implicitDependenciesLabel}`;

	const hasInteractiveContent =
		hasImplicitDependencies || ((hasDependencies || hasDependees) && onUnlink !== undefined);

	const content = (
		<FocusLock returnFocus={false}>
			<div
				css={[
					containerStyles,
					hasImplicitDependencies && containerWideWidthStyles,
					!hasImplicitDependencies && containerNotWideWidthStyles,
				]}
				data-testid="roadmap.timeline-table-kit.ui.chart-timeline-overlay.dependency-flyout.container"
				role="dialog"
				tabIndex={hasInteractiveContent ? -1 : 0}
				ref={dialogContentRef}
				aria-label={flyoutAriaLabel}
			>
				<div css={[containerNotWideWidthStyles, flexStyles]}>
					{(hasDependencies || hasDependees) && (
						<Box>
							{hasImplicitDependencies && (
								<Box xcss={styledDependencyStyles}>
									<Heading as="h2" size="xsmall">
										<Text weight="regular">
											<FormattedMessage
												{...messages.dependencyOf}
												values={{
													itemKey,
													title,
													// @ts-expect-error  TS2322: Type '(chunks: string) => JSX.Element' is not assignable to type 'ReactNode'.
													b: (chunks: string) => <b>{chunks}</b>,
												}}
											/>
										</Text>
									</Heading>
								</Box>
							)}
							{renderDepsBlock(dependencies, INCOMING, inwardDependencyDescription)}
							{hasDependencies && hasDependees && <Line />}
							{renderDepsBlock(dependees, OUTGOING, outwardDependencyDescription)}
						</Box>
					)}
					{hasImplicitDependencies && (
						<Box>
							<ImplicitDependency
								parentItem={item}
								implicitDependencyItems={implicitDependencyItems}
								onClose={onClose}
								{...(onUnlink
									? {
											onUnlink: onUnlinkClick,
										}
									: undefined)}
								inwardDependencyDescription={inwardDependencyDescription}
								outwardDependencyDescription={outwardDependencyDescription}
							/>
						</Box>
					)}
				</div>
			</div>
		</FocusLock>
	);

	/* NOTE: providing a key here is a hack !!
	 * The flyout does not re-render just because its anchor changed position (if it is already open).
	 * Instead, we must force it to re-render if we would be showing it for a different item.
	 */
	return (
		<OverlayFlyout
			key={id}
			position={position}
			content={content}
			onClose={fg('timeline_grid_navigation_m2') ? handleClose : onClose}
			triggerRef={triggerRef}
		/>
	);
};

RoadmapDependencyFlyout.defaultProps = {
	dependencies: [],
	dependees: [],
	// eslint-disable-next-line @typescript-eslint/no-empty-function
	onClose: () => {},
};

const containerWideWidthStyles = css({
	width: '312px',
});

const containerNotWideWidthStyles = css({
	width: '280px',
});

const flexStyles = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.250', '20px'),
});

const containerStyles = css({
	paddingTop: token('space.250', '20px'),
	paddingRight: token('space.250', '20px'),
	paddingBottom: token('space.250', '20px'),
	paddingLeft: token('space.250', '20px'),
	backgroundColor: token('elevation.surface.overlay', N20),
	boxShadow: token('elevation.shadow.overlay', '0 2px 4px 0 rgba(0, 0, 0, 0.25)'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	borderRadius: `${borderRadius}px`,
	maxHeight: '300px',
	overflow: 'auto',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	zIndex: CHART_FLYOUT_Z_INDEX,
});

const styledDependencyStyles = xcss({
	marginBottom: 'space.100',
});

export { RoadmapDependencyFlyout };
